import React from "react";
import { Link } from "react-router-dom";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { ArrowRight, Check } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

const PILLARS = [
  { title: "Starter package", body: "Every approved grower receives a starter package with packaging, promotional materials for your farm, and our guide to how we do things." },
  { title: "No buy in. No scheme.", body: "This isn't franchising. You operate as an independent contractor. Your income, your tax, your GST. We bring the customers and the brand." },
  { title: "Run by you, your way", body: "You set the area you service, your delivery days, and your prices. Your customers will choose you from the list." },
];

const STEPS = [
  "Read the Founding Grower Prospectus",
  "Pass the short Knowledge Check",
  "Sign the Acknowledgement of Understanding",
  "Complete a 7-step application (~15 minutes)",
  "We review within 7 business days",
  "Sign your Founding Grower Agreement & begin",
];

export default function BecomeAGrowerPage() {
  const { t } = useSiteContent();
  return (
    <div className="club-theme min-h-screen" data-testid="become-grower-page">
      <PublicHeader />
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-12">
        <div className="bloom-divider mb-6">✦ {t("grower.eyebrow", "Chapter I · Become a Grower")} ✦</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[0.95] max-w-3xl">{t("grower.headline", "Grow with us.")}</h1>
        <p className="font-serif italic text-xl text-[var(--bloom-ink-soft)] mt-6 max-w-2xl">
          {t("grower.sub", "We're carefully accepting Founding Growers one region at a time. If you grow with care, we'd love to talk.")}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/register" className="bloom-btn bloom-btn-primary" data-testid="apply-cta">{t("grower.cta", "Begin your application")} <ArrowRight size={14} /></Link>
          <Link to="/login" className="bloom-btn bloom-btn-outline" data-testid="signin-cta">Existing grower sign in</Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {PILLARS.map((p) => (
            <div key={p.title} className="border-l-2 border-[var(--bloom-brand)] pl-6">
              <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
              <p className="font-serif italic text-base text-[var(--bloom-ink-soft)] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[var(--bloom-surface)] py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bloom-divider mb-6">✦ The journey ✦</div>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight mb-10">From curiosity to <em>Founding Grower</em>.</h2>
          <ol className="space-y-4">
            {STEPS.map((s, i) => (
              <li key={i} className="flex items-start gap-4 border-b border-[var(--bloom-border)] pb-4">
                <span className="font-serif italic text-2xl text-[var(--bloom-brand)] w-10">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-serif text-lg leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
          <div className="mt-10 text-center">
            <Link to="/register" className="bloom-btn bloom-btn-primary">Begin your application <ArrowRight size={14} /></Link>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
